import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { MapPin, Phone, Mail, Calendar, Facebook, Instagram, Youtube, Lock } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'

export function Footer() {
  const { t } = useTranslation()
  const currentYear = new Date().getFullYear()

  const quickLinks = [
    { key: 'breastSurgery', labelKey: 'services.breastSurgery' },
    { key: 'bodySurgery', labelKey: 'services.bodySurgery' },
    { key: 'faceSurgery', labelKey: 'services.faceSurgery' },
    { key: 'faceAesthetic', labelKey: 'services.faceAesthetic' },
    { key: 'regenerative', labelKey: 'services.regenerative' },
    { key: 'laser', labelKey: 'services.laser' },
  ] as const

  const mapUrl =
    'https://www.google.com/maps/embed?origin=mfe&pb=!1m2!2m1!1sAD+Aesthetics+Clinic%2C+Via+Gazzera+1%2C+Verona'

  return (
    <footer className="bg-ink-deep text-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Column 1: Brand */}
          <div className="space-y-4">
            <LocalizedLink to="home" className="inline-block">
              <img
                src="/media/logos/logo-footer.svg"
                alt="AD Aesthetics"
                className="h-16 w-auto"
              />
            </LocalizedLink>
            <p className="text-sm text-white/50 leading-relaxed">
              {t('footer.medicalDisclaimer', 'Interventi eseguiti da medici specialisti in chirurgia plastica ed estetica.')}
            </p>
          </div>

          {/* Column 2: Quick links */}
          <div>
            <h3 className="font-display text-lg font-semibold text-white mb-4">
              {t('footer.navigation', 'Navigazione')}
            </h3>
            <ul className="space-y-2">
              {quickLinks.map(({ key, labelKey }) => (
                <li key={key}>
                  <LocalizedLink
                    to={key}
                    className="text-sm text-white/70 hover:text-primary transition-colors"
                  >
                    {t(labelKey)}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Contacts */}
          <div>
            <h3 className="font-display text-lg font-semibold text-white mb-4">
              {t('footer.contacts', 'Contatti')}
            </h3>
            <ul className="space-y-3">
              <li>
                <a
                  href={mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-sm text-white/70 hover:text-primary transition-colors"
                >
                  <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>
                    Via Gazzera, 1
                    <br />
                    37128 Verona VR
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="tel:+390458348902"
                  className="flex items-center gap-3 text-sm text-white/70 hover:text-primary transition-colors"
                >
                  <Phone className="w-4 h-4 text-primary" />
                  +39 045 834 8902
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@adaesthetics.it"
                  className="flex items-center gap-3 text-sm text-white/70 hover:text-primary transition-colors"
                >
                  <Mail className="w-4 h-4 text-primary" />
                  info@adaesthetics.it
                </a>
              </li>
              <li>
                <LocalizedLink
                  to="bookConsultation"
                  className="flex items-center gap-3 text-sm text-white/70 hover:text-primary transition-colors"
                >
                  <Calendar className="w-4 h-4 text-primary" />
                  {t('buttons.bookConsultation', 'Prenota consulenza')}
                </LocalizedLink>
              </li>
            </ul>
          </div>

          {/* Column 4: Social & Legal */}
          <div>
            <h3 className="font-display text-lg font-semibold text-white mb-4">
              {t('footer.followUs', 'Seguici')}
            </h3>
            <div className="flex items-center gap-4 mb-6">
              <a
                href="https://www.facebook.com/adaestheticsclinic"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="p-2 rounded-full border border-white/20 text-white/70 hover:text-primary hover:border-primary transition-colors"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="https://www.instagram.com/adaestheticsclinic/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="p-2 rounded-full border border-white/20 text-white/70 hover:text-primary hover:border-primary transition-colors"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://www.youtube.com/channel/UCZ8GTCahKChAQdfawiU5-BQ"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="p-2 rounded-full border border-white/20 text-white/70 hover:text-primary hover:border-primary transition-colors"
              >
                <Youtube className="w-4 h-4" />
              </a>
            </div>
            <LocalizedLink
              to="blog"
              className="inline-block text-sm text-white/70 hover:text-primary transition-colors"
            >
              {t('nav.blog')}
            </LocalizedLink>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/50">
          <p className="flex items-center gap-2 flex-wrap">
            © {currentYear} AD Aesthetics. {t('footer.allRightsReserved', 'Tutti i diritti riservati.')}
            <Link
              to="/login"
              className="inline-flex items-center justify-center p-1.5 rounded-md text-white/50 hover:text-primary transition-colors"
              aria-label={t('aria.reservedArea', 'Area riservata')}
            >
              <Lock className="w-4 h-4" />
            </Link>
            <span className="text-white/30">|</span>
            <span>
              sviluppato da{' '}
              <a
                href="https://novaui.it/it"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors"
              >
                NovaUI
              </a>
            </span>
          </p>
          <div className="flex flex-col md:flex-row items-center gap-3 md:gap-4 text-center md:text-right">
            <LocalizedLink
              to="privacy"
              className="hover:text-primary transition-colors"
            >
              {t('footer.privacy', 'Privacy')}
            </LocalizedLink>
            <span className="hidden md:inline text-white/30">|</span>
            <LocalizedLink
              to="cookiePolicy"
              className="hover:text-primary transition-colors"
            >
              {t('footer.cookiePolicy', 'Cookie Policy')}
            </LocalizedLink>
          </div>
        </div>
      </div>
    </footer>
  )
}
